import Seo from "@/components/Seo";
import { localBusinessSchema, breadcrumbSchema, serviceSchema } from "@/data/site";
import { PageHero, Section, CtaBand } from "@/components/sections";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion";
import { GoldButton, WhatsAppButtonLight } from "@/components/cta";
import { BirdMark } from "@/components/brand";

const features = [
  "Rooms and rates shown beautifully on mobile",
  "Direct booking requests by form and WhatsApp",
  "Take deposits online (via Paystack / Flutterwave) — less OTA commission",
  "Google Maps, directions and guest reviews",
  "Restaurant, events and airport-transfer information",
];

export default function Hotels() {
  return (
    <>
      <Seo
        title="Hotel Website Design & Direct Booking in Ghana — Sankofa Launch Studio"
        description="We build fast, mobile-first websites for guest houses, small hotels, resorts and serviced apartments in Ghana. Show rooms beautifully, take direct booking requests, and reduce OTA commission."
        jsonLd={[
          localBusinessSchema(),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "For Hotels", path: "/hotels" }]),
          serviceSchema("Hotel website design", "Mobile-first hotel websites with direct booking requests and online deposits."),
        ]}
      />
      <PageHero
        eyebrow="For Hotels"
        breadcrumb="Home / For Hotels"
        title="Websites and direct bookings for Ghanaian hotels"
        intro="We build fast, mobile-first websites for guest houses, small hotels, resorts and serviced apartments in Ghana. Show your rooms beautifully, take direct booking requests, and reduce commission paid to online travel agencies."
      />

      <Section className="bg-cream">
        <div className="grid grid-cols-1 items-start gap-10 md:grid-cols-2 md:gap-16">
          <div>
            <Reveal>
              <h2 className="font-serif text-3xl font-600 text-navy md:text-4xl">What your hotel website includes</h2>
            </Reveal>
            <StaggerGroup className="mt-8 space-y-4">
              {features.map((f) => (
                <StaggerItem key={f}>
                  <div className="flex items-start gap-4 rounded-2xl bg-white p-5 shadow-soft">
                    <BirdMark className="mt-0.5 h-6 w-6 shrink-0" alt="" />
                    <p className="leading-relaxed text-ink">{f}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <GoldButton size="lg" testid="hotels-book-btn">Book a free hotel briefing</GoldButton>
              <WhatsAppButtonLight size="lg" text="Hello Sankofa, I'd like a website for my hotel." testid="hotels-whatsapp-btn" />
            </div>
          </div>
          <Reveal>
            <div className="overflow-hidden rounded-2xl shadow-softlg">
              <img
                src="https://images.unsplash.com/photo-1765910639943-889b5cab5be3?crop=entropy&cs=srgb&fm=jpg&q=70&w=1200&ixlib=rb-4.1.0"
                alt="Warm resort pool with sun loungers and palm trees"
                loading="lazy"
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      <CtaBand title="Let's build your hotel's website" waText="Hello Sankofa, I'd like a website for my hotel." />
    </>
  );
}
